import React, { useMemo } from 'react'
import styled from 'styled-components'
import { TextTooltip } from '@components'
import { IPISPaymentStatus } from '@query'
import { useTranslation } from 'react-i18next'
import { PISStatusTimeline } from './PISStatusTimeline'

interface Props {
  statuses?: IPISPaymentStatus[]
}

export const PISPaymentStatusBadge: React.FC<Props> = ({ statuses }) => {
  const [t] = useTranslation()

  const sortedStatuses = useMemo(() => {
    const items = statuses ?? []
    return [...items].sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    )
  }, [statuses])

  const latestStatus = sortedStatuses[sortedStatuses.length - 1]

  const getStatusLabel = (status: IPISPaymentStatus) => {
    if (status.status_name) {
      return t(status.status_name)
    }
    return status.status
  }

  const getStatusDescription = (status: IPISPaymentStatus) => {
    if (status.status_description) {
      return t(status.status_description)
    }
    return null
  }

  const getBadgeTone = (statusIntent: IPISPaymentStatus['status_intent']) => {
    switch (statusIntent) {
      case 'success':
        return 'success'
      case 'warning':
        return 'warning'
      case 'danger':
        return 'error'
      case 'info':
        return 'info'
      default:
        return 'default'
    }
  }

  if (!latestStatus) {
    return null
  }

  const tone = getBadgeTone(latestStatus.status_intent)

  return (
    <TextTooltip
      tooltip={
        <PISStatusTimeline
          statuses={sortedStatuses}
          getStatusLabel={getStatusLabel}
          getStatusDescription={getStatusDescription}
        />
      }
      placement="bottom-start"
      maxWidth={320}
    >
      <BadgeContainer $tone={tone}>
        <BadgeLeft $tone={tone}>
          {t('invoicing.purchaseInvoiceDetails.pisPaymentBadgeTitle')}
        </BadgeLeft>
        <BadgeRight $tone={tone}>{getStatusLabel(latestStatus)}</BadgeRight>
      </BadgeContainer>
    </TextTooltip>
  )
}

type StatusTone = 'info' | 'success' | 'error' | 'warning' | 'default'

const getToneColor = (tone: StatusTone, theme: any) => {
  switch (tone) {
    case 'success':
      return theme.colors.success
    case 'warning':
      return theme.colors.warning
    case 'error':
      return theme.colors.error
    case 'info':
      return theme.colors.metalGray
    default:
      return theme.colors.metalGray
  }
}

const BadgeContainer = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid ${({ theme, $tone }) => getToneColor($tone, theme)};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
  cursor: help;
`

const BadgeLeft = styled.span<{ $tone: StatusTone }>`
  color: ${({ theme, $tone }) => getToneColor($tone, theme)};
  background: ${({ theme }) => theme.colors.neutralWhite};
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
`

const BadgeRight = styled.span<{ $tone: StatusTone }>`
  background: ${({ theme, $tone }) => getToneColor($tone, theme)};
  color: ${({ theme }) => theme.colors.neutralWhite};
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
`
